import { useEffect, useState } from 'react';
import { IconRefresh, IconSearch, IconCircleCheck, IconClock, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { StatCard } from '../components/cards';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface FileTransferInfo {
  fileName: string;
  size: number;
  uploadedAt: string;
  path: string;
  status: 'transferred' | 'pending';
}

interface BillingReconSummary {
  totalFiles: number;
  totalSize: number;
  transferredFiles: number;
  pendingFiles: number;
  lastSyncDate?: string;
  files: FileTransferInfo[];
}

export function MtnBillingReconPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<BillingReconSummary | null>(null);
  const [searchPrefix, setSearchPrefix] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const loadSummary = async () => {
    setLoading(true);
    setError('');

    try {
      const params: any = {};
      if (searchPrefix) params.prefix = searchPrefix;

      const res = await api.get('/mtn/billing-recon/summary', { params });
      setSummary(res.data.data);
      setLastUpdatedAt(new Date());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load billing reconciliation data');
      console.error('Error loading billing recon:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleSearch = () => {
    loadSummary();
  };

  const handleClearSearch = () => {
    setSearchPrefix('');
    setTimeout(() => {
      loadSummary();
    }, 100);
  };

  return (
    <div>
      <PageHeader
        title="MTN Billing Reconciliation"
        subtitle="Monitor file transfers from MTN SFTP to Google Cloud Storage to ensure all files have been transferred"
        action={
          <div className="flex items-center gap-4">
            {lastUpdatedAt && (
              <span className="text-sm text-muted-foreground">
                Last updated: {lastUpdatedAt.toLocaleTimeString()}
              </span>
            )}
            <Button variant="outline" onClick={loadSummary} disabled={loading}>
              {loading ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconRefresh className="mr-2 h-4 w-4" />}
              Refresh
            </Button>
          </div>
        }
      />

      <MainCard className="mb-6">
        <div className="flex items-center gap-4">
          <Input
            value={searchPrefix}
            onChange={(e) => setSearchPrefix(e.target.value)}
            placeholder="e.g., billing/2024/"
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
          <Button onClick={handleSearch} disabled={loading}>
            <IconSearch className="mr-2 h-4 w-4" />
            Search
          </Button>
          {searchPrefix && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>
      </MainCard>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-sm underline ml-4">Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      {summary && (
        <div className="flex gap-3 mb-6 w-full">
          <div className="flex-1 min-w-0">
            <StatCard title="Total Files" value={summary.totalFiles.toLocaleString()} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard title="Total Size" value={formatFileSize(summary.totalSize)} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard title="Transferred" value={summary.transferredFiles.toLocaleString()} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard
              title="Last Sync"
              value={summary.lastSyncDate ? formatDate(summary.lastSyncDate) : 'No files found'}
            />
          </div>
        </div>
      )}

      <MainCard>
        <h3 className="text-lg font-semibold mb-4">Files in Google Cloud Storage</h3>

        {loading && !summary ? (
          <div className="flex justify-center p-8">
            <IconLoader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : summary && summary.files.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File Name</TableHead>
                <TableHead>Path</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead>Uploaded At</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.files.map((file, index) => (
                <TableRow key={index} className="hover:bg-muted/50">
                  <TableCell>
                    <span className="text-sm font-medium">{file.fileName}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground font-mono">{file.path}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm">{formatFileSize(file.size)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDate(file.uploadedAt)}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={file.status === 'transferred' ? 'success' : 'warning'}
                    >
                      {file.status === 'transferred' ? (
                        <><IconCircleCheck className="mr-1 h-3 w-3" /> Transferred</>
                      ) : (
                        <><IconClock className="mr-1 h-3 w-3" /> Pending</>
                      )}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Alert>
            <AlertDescription>
              {searchPrefix
                ? `No files found matching prefix: ${searchPrefix}`
                : 'No files found in the billing reconciliation bucket'}
            </AlertDescription>
          </Alert>
        )}
      </MainCard>
    </div>
  );
}
